import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '@components/lib/utils';
import './ProfileMainTabs.css';

import TabsList from './TabsList';
import FixedTabs from './FixedTabs';
import useScrollPosition from './useScrollPosition';
import { ProfileMainTabsProps } from './types';

const ProfileMainTabs: React.FC<ProfileMainTabsProps> = ({
  userId: _userId,
  userBlockHeight: _userBlockHeight,
  username = '',
  auths: _auths,
  isMe,
  isPreview,
  isOnboarding = false,
  colorMode = 'classic',
  advancedColor = null,
  tabs = [],
  links: _links,
  linkIcons,
  isLoggedIn,
  personaInfo: _personaInfo,
}) => {
  const { pathname } = useLocation();
  const [value, setValue] = useState<number>(0);
  const [tempValue, setTempValue] = useState<number>(0);
  const [fade, setFade] = useState<boolean>(false);
  const [tab] = useState<string>('instagram');
  const [tabsPosition] = useState<string>('sticky');
  const [currentTabsScroll] = useState<Record<string, number>>({});

  useScrollPosition();

  useEffect(() => {
    if (currentTabsScroll[tab] && typeof window === 'object') {
      window.scrollTo(0, currentTabsScroll[tab]);
    }
  }, [currentTabsScroll, tab]);

  useEffect(() => {
    const product = tabs.find(({ type }) => type === 'product');

    if (pathname.includes('/products/') && product) {
      setTempValue(product?.order ?? 0);
      setValue(product?.order ?? 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    tempValue === 0 && value === 0 && fade && setFade(false);
  }, [tempValue, value, fade]);

  return useMemo(
    () => (
      <div
        className={cn(
          'mx-auto w-full max-w-[540px] transition-opacity duration-150',
          'max-[1279px]:w-full max-[1279px]:max-w-none max-[1279px]:pb-5',
          fade ? 'opacity-0' : 'opacity-100',
        )}
      >
        <div
          id="tabs-container"
          className="mb-2.5 flex w-full items-start [&>div]:w-fit"
        >
          <div className="relative flex w-auto overflow-x-scroll [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <TabsList
              tabs={tabs}
              tempValue={tempValue}
              value={value}
              setFade={setFade}
              setTempValue={setTempValue}
              setValue={setValue}
              advancedColor={advancedColor}
            />
          </div>
          <div className="flex-1 self-end border-b-[1.5px] border-[#aaa]" />
        </div>
        <FixedTabs
          tabs={tabs}
          tempValue={tempValue}
          value={value}
          setFade={setFade}
          setTempValue={setTempValue}
          setValue={setValue}
          colorMode={colorMode}
          isOnboarding={isOnboarding}
          isLoggedIn={isLoggedIn}
          isPreview={isPreview}
        />
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isMe,
      tabsPosition,
      username,
      value,
      isPreview,
      tabs,
      colorMode,
      isOnboarding,
      linkIcons,
    ],
  );
};

export default ProfileMainTabs;
